import { ComponentType, useContext } from 'react';
import { AppContext } from '../../sporttia/AppContext';
import constants from '../../config/constants';
import {
    ACCOUNT_PATH,
    canAccessRoute,
    hasNoPermissions,
} from '../../config/routePermissions';
import Page401 from '../../pages/Page401';
import RedirectToLogin from '../RedirectToLogin';
import NoPermissionsScreen from './NoPermissionsScreen';

type ProtectedElementProps = {
    path: string;
    component: ComponentType;
    routeRole?: string;
    userRole?: string;
    exact: boolean;
};

/**
 * Protected element wrapper.
 * En una ruta protegida pueden ocurrir dos cosas:
 *    1. Si el usuario está logueado y tanto la ruta como el usuario tienen el mismo rol le carga dicha ruta. La excepción
 *    es para el caso en el que el rol sea SPORTCENTER. En ese caso se hace un segundo filtrado (para evitar el bypass del
 *    menu lateral escribiendo la ruta directamente en el navegador): con los roles encendidos, por el permiso v8 de la
 *    sección (`routePermissions`); con los roles apagados, por los privilegios del administrador. Con los roles
 *    encendidos y ningún permiso (sin rol o con el rol desactivado) se enseña la pantalla de "sin acceso" en todas
 *    las páginas menos en su cuenta.
 *    2. Si el usuario está logueado, pero la ruta y el usuario no tienen el mismo rol se le muestra la página
 *    de acceso no autorizado.
 *    3. Si el usuario no está logueado y quiere acceder a una ruta protegida primero se lleva a la página de
 *    login y luego se le redirige a la página que había pedido.
 */
export default function ProtectedElement({
    path,
    component: Component,
    ...rest
}: ProtectedElementProps): React.ReactElement | null {
    const cxt = useContext(AppContext)!;

    // If user has token but user data hasn't loaded yet, don't redirect to login
    // The AppProvider loader should handle this, but this is an extra safety check
    if (cxt?.logged && !('userRole' in rest) && 'routeRole' in rest) {
        return null; // Wait for user data to load
    }

    if (
        cxt?.logged &&
        'routeRole' in rest &&
        'userRole' in rest &&
        rest.routeRole === rest.userRole
    ) {
        if (rest.userRole === constants.roles.sportcenter) {
            const access = {
                rolesEnabled: cxt.rolesEnabled,
                permissions: cxt.permissions,
                privileges: cxt.privileges ?? [],
            };

            if (hasNoPermissions(access) && path !== ACCOUNT_PATH) {
                return <NoPermissionsScreen />;
            }

            if (canAccessRoute(path, access)) {
                return <Component />;
            }
        } else {
            return <Component />;
        }

        return <Page401 />;
    }

    if (
        cxt.logged &&
        'routeRole' in rest &&
        'userRole' in rest &&
        rest.routeRole !== rest.userRole
    ) {
        return <Page401 />;
    }

    return <RedirectToLogin />;
}
